import { ForwardedRef, useCallback, useMemo, useState } from "react";
import Select, {
  GroupBase,
  MultiValue,
  OnChangeValue,
  SelectInstance,
  SingleValue,
  StylesConfig,
} from "react-select";
import { SELECT_STYLES } from "src/constants/selectStyle";

import { VirtualOption } from "./types";

export interface VirtualMultiSelectProps<T, isMulti extends boolean = false>
  extends Omit<VirtualSelectProps<T, isMulti>, "value" | "onChange"> {
  value?: isMulti extends true ? T[] : T;
  onChange: (value?: OnChangeValue<T, isMulti>) => void;
  forwardedRef?: ForwardedRef<
    SelectInstance<VirtualOption<T>, isMulti, GroupBase<VirtualOption<T>>>
  >;
}

function isMultiValue<T>(
  arg: MultiValue<T> | SingleValue<T>,
): arg is MultiValue<T> {
  return Array.isArray(arg);
}

export const VirtualMultiSelect = <T, isMulti extends boolean = false>(
  props: VirtualMultiSelectProps<T, isMulti>,
) => {
  const onChange = useCallback(
    (value?: OnChangeValue<VirtualOption<T>, isMulti>) => {
      if (isMultiValue(value)) {
        const values = value.map((item) => item.value);

        return props.onChange(values as OnChangeValue<T, isMulti>);
      } else {
        props.onChange(value?.value as OnChangeValue<T, isMulti>);
      }
    },
    [props],
  );

  const value = useMemo(() => {
    if (props.isMulti && Array.isArray(props.value)) {
      const arrValue = props.value;

      return props.options.filter((item) =>
        arrValue.includes(item.value),
      ) as unknown as OnChangeValue<VirtualOption<T>, isMulti>;
    }

    return props.options.find(
      (item) => item.value === props.value,
    ) as OnChangeValue<VirtualOption<T>, isMulti>;
  }, [props.isMulti, props.options, props.value]);

  return (
    <VirtualCustomSelect<T, isMulti>
      {...props}
      onChange={onChange}
      value={value}
      forwardedRef={props.forwardedRef}
    />
  );
};

export interface VirtualSelectProps<T, isMulti extends boolean = false> {
  options: VirtualOption<T>[];
  placeholder?: string;
  isLoading?: boolean;
  drawCount?: number;
  isMulti?: isMulti;
  onChange: (value?: OnChangeValue<VirtualOption<T>, isMulti>) => void;
  value?: OnChangeValue<VirtualOption<T>, isMulti>;
  styles?: StylesConfig<VirtualOption<T>, isMulti>;
  forwardedRef?: ForwardedRef<
    SelectInstance<VirtualOption<T>, isMulti, GroupBase<VirtualOption<T>>>
  >;
}

export const VirtualCustomSelect = <T, isMulti extends boolean>({
  options = [],
  drawCount = 200,
  isLoading = false,
  ...props
}: VirtualSelectProps<T, isMulti>) => {
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    const data = [];
    for (const iterator of options) {
      if (data.length > drawCount) break;
      if (currentValueIncludesSearchValue(search, iterator.label))
        data.push(iterator);
    }
    return data;
  }, [drawCount, options, search]);

  return (
    <Select<VirtualOption<T>, isMulti>
      isLoading={isLoading}
      styles={SELECT_STYLES}
      options={filteredOptions}
      {...props}
      onInputChange={setSearch}
      ref={props.forwardedRef}
    />
  );
};

const currentValueIncludesSearchValue = (
  searchValue: string,
  currentValue: string,
) => {
  return currentValue.toLowerCase().includes(searchValue.toLowerCase());
};
